import type {
  PackGoSettings,
  PackGoStatuses,
  PackGoStatus,
  PackGoInclusionExclusionType,
  PackGoInclusionExclusionStatuses,
  PackGoStatusType,
  PackGoFiltersRequestPayload,
  PackGoFiltersChoiceRequestPayload,
} from '@/types/packgo.models'
import type { Integration } from '@/types/models'
import axios from '@/utils/axios'
import type { AxiosResponse } from 'axios'

const STATUS_TYPES: PackGoStatusType[] = ['order', 'payment']

export type InitializedFilter = ReturnType<typeof initializeFilters>[number]

/**
 * Build the main filters model to be used in the orders queue settings page.
 * The resulting data structure is something like:
 *
 * filters = [{integration: Object, payment: Object, order: Object}, {...}]
 *
 * Where:
 * `integration`: is an Integration instance
 * `payment` and `order` are objects with both the statuses list and the
 * selected items.
 * E.g:
 * ```
 * vm.filters = [
 *     {
 *         integration: {id: 1, shop_name: 'Test Shop', ...},
 *         payment: {statuses: [], selection: []},
 *         order: {
 *             statuses: [{id: 1, label: 'Pending'},
 *                        {id: 2, label: 'Payment accepted'}],
 *             selection: [{id: 2, label: 'Payment accepted'}]
 *         },
 *     }
 * ];
 * ```
 */
const initializeFilters = (
  statuses: PackGoStatuses,
  exclusions: PackGoInclusionExclusionStatuses,
  inclusions: PackGoInclusionExclusionStatuses,
  integrations: Integration[],
) => {
  function fromPairs(arr: Array<string | string[]>) {
    return arr.reduce<Record<string, string>>((accumulator, value) => {
      accumulator[value[0]] = value[1]
      return accumulator
    }, {})
  }

  /**
   * Return an object of key/value pairs based on the statuses arrays with
   * empty status keys removed. Since we convert the arrays to an object, all
   * duplicates will be also removed.
   */
  function toObject(statusList: Array<string | string[]>) {
    return fromPairs(statusList.filter(status => status[0] !== undefined))
  }

  function prepareStatuses(integration: Integration | undefined, value: PackGoStatus, type: keyof PackGoStatus) {
    const statusObject = toObject(value[type])
    const integrationId = integration ? +integration.id : null

    return {
      statuses: Object.keys(statusObject).length === 0 ? null : statusObject,
      // Pick all items from the status map minus the excluded ones.
      inclusions:
        integrationId && inclusions[integrationId] && inclusions[integrationId][type]
          ? inclusions[integrationId][type]
          : [],
      exclusions:
        integrationId && exclusions[integrationId] && exclusions[integrationId][type]
          ? exclusions[integrationId][type]
          : [],
    }
  }

  function initialize([key, value]: [key: string, value: PackGoStatus]) {
    const integration = integrations.find(integration => integration.id === parseInt(key))

    return {
      integration,
      payment: prepareStatuses(integration, value, 'payment'),
      order: prepareStatuses(integration, value, 'order'),
    }
  }

  return Object.entries(statuses).map(initialize)
}

export default {
  async getSettings() {
    const { data } = await axios.get<PackGoSettings | undefined>('/orders/packgo/settings')
    return data
  },

  async updateSettings(settings: Partial<PackGoSettings>) {
    const { data } = await axios.patch<PackGoSettings | undefined>('/orders/packgo/settings', settings)
    return data
  },

  async updateDynamicFilters(
    payload: PackGoFiltersChoiceRequestPayload,
  ): Promise<AxiosResponse> {
    return axios.post('/orders/packgo/settings/filters/choice', payload)
  },

  initializeFilters,

  createFiltersPayload(filters: InitializedFilter[]): PackGoFiltersRequestPayload {
    function statusDiff(allStatuses: string[], selection: string[]) {
      if (allStatuses.length === 0) {
        return []
      }

      return allStatuses.filter(status => !selection.includes(status))
    }

    const getDataFromFilters = (type: PackGoInclusionExclusionType) => {
      const data: PackGoInclusionExclusionStatuses = {}
      filters.forEach((filter) => {
        const rawIntegrationId = filter.integration?.id ?? undefined
        if (rawIntegrationId === undefined) {
          return
        }
        const integrationId = +rawIntegrationId
        data[integrationId] = {
          order: [],
          payment: [],
        }
        STATUS_TYPES.forEach((statusType) => {
          if (type === 'exclusions') {
            const statuses = filter[statusType].statuses
            const statusKeys = statuses ? Object.keys(statuses) : []
            data[integrationId][statusType] = statusDiff(statusKeys, filter[statusType].inclusions)
          } else {
            data[integrationId][statusType] = filter[statusType][type]
          }
        })
      })
      return data
    }

    return {
      inclusions: getDataFromFilters('inclusions'),
      exclusions: getDataFromFilters('exclusions'),
    }
  },
}
